import React, { useEffect, useMemo } from "react";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { literalUnion } from "diagnostics-lib/io";
import t from "../../../../../../i18n";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { styled } from "@linaria/react";
import { color } from "@ui";
import { spacings } from "@ui/Theme";
import { type Quantity, type QuantityIndex } from "../../../../../../api/useQuantities";
import Ellipsis from "@ui/Ellipsis";
import formatNumber from "@ui/i18n/formatNumber";
import { useProjectCategories } from "../../../../../../api/useProjectCategories";
import { NEW_FORMULA_TMP_ID } from "../../utils/constants";
import {
  CalculatorLine,
  DeleteBinLine,
  EyeLine,
  Formula,
  ListCheck,
  LockLine,
  PencilLine,
  Settings3Line,
} from "@ui/IconV2/react";
import type { MakeOptional } from "diagnostics-lib/typescript";
import { useRevalidate } from "../../../../../../api/useQuery";
import usePrevious from "@ui/lib/hooks/usePrevious";
import { useCanUserDoOnProject } from "../../../../../canUserDo";
import { useSetups } from "../../../../../../api/useSetups";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { css } from "@linaria/core";
import ButtonIconV2 from "@ui/ButtonIconV2";
import DashboardLine from "@ui/IconV2/react/DashboardLine";
import { getCheckQuantityUsedInSetupsOrFormulas } from "diagnostics-domain/quantity/usage";
import Button from "@ui/Button";
import type { FormulaVersion } from "../../../../../../api/quantity/formula-version";
import { getCurrentFormulaVersion } from "../../../../../../domain/formula-version";
const StyledLockLine = styled(LockLine)`
  display: flex;
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  color: ${color.carbon800};
`;

const NameContainer = styled.div`
  display: flex;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: auto;
  margin-right: auto;
  align-items: center;
`;

const PercentageIcon = styled(CalculatorLine)`
  height: 15px;
  width: 15px;
  margin-top: -2px;
  color: ${color.carbon800};
`;

const IconContainer = styled.div`
  display: flex;
  width: 15px;
  margin-right: 10px;
`;

const NameEllipsis = styled(Ellipsis)`
  padding-right: ${spacings.xs};
`;

const FormulaContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const FormulaEllipsis = styled(Ellipsis)`
  padding-right: ${spacings.xs};
`;

const AutoMarginLockLine = styled(StyledLockLine)`
  margin-left: auto;
`;

const CenteredSensorDiv = styled.div`
  align-self: center;
`;

export const computeRowKey = (quantity: { id: string }) => `quantity-id-${quantity.id}`;

export const columns = [
  "usage",
  "name",
  "hidden",
  "description",
  "unit",
  "formula",
  "sensorTag",
  "min",
  "max",
  "maxStd",
  "system",
  "equipments",
  "equipmentType",
  "actions",
] as const;
export type Column = (typeof columns)[number];

export const columnsWithoutActions = columns.filter(
  (c): c is Exclude<Column, "actions"> => c !== "actions",
);

const defaultVisibleColumns: Column[] = [
  "usage",
  "name",
  "description",
  "unit",
  "formula",
  "min",
  "max",
  "maxStd",
  "system",
  "equipments",
  "equipmentType",
  "actions",
];

export const getLabel = (column: Column) => {
  switch (column) {
    case "usage":
      return "";
    case "name":
      return t("setup.paragraphs.quantities.table.definitions.column.name");
    case "hidden":
      return t("setup.paragraphs.quantities.table.definitions.column.hidden");
    case "description":
      return t("setup.paragraphs.quantities.table.definitions.column.description");
    case "unit":
      return t("setup.paragraphs.quantities.table.definitions.column.unit");
    case "formula":
      return t("setup.paragraphs.quantities.table.definitions.column.formula");
    case "sensorTag":
      return t("setup.paragraphs.quantities.table.definitions.column.sensorTag");
    case "min":
      return t("setup.paragraphs.quantities.table.definitions.column.min");
    case "max":
      return t("setup.paragraphs.quantities.table.definitions.column.max");
    case "maxStd":
      return t("setup.paragraphs.quantities.table.definitions.column.maxStd");
    case "system":
      return t("setup.paragraphs.quantities.table.definitions.column.system");
    case "equipments":
      return t("setup.paragraphs.quantities.table.definitions.column.equipments");
    case "equipmentType":
      return t("setup.paragraphs.quantities.table.definitions.column.equipmentType");
    case "actions":
      return t("setup.paragraphs.quantities.table.definitions.column.actions");
    default:
      throw new Error(column satisfies never);
  }
};

const useColumns = createGlobalState({
  storage: "session",
  key: "quantitiesColumns",
  type: io.array(literalUnion(columns)),
  defaultValue: defaultVisibleColumns,
  scoped: true,
});

export const getQuantitiesColumnsType = (canUserEdit: boolean) =>
  io.array(
    literalUnion(
      canUserEdit
        ? columns
        : (columns.filter(c => c !== "actions") as Exclude<Column, "actions">[]),
    ),
  );

export const getQuantitiesDefaultColumns = (canUserEdit: boolean): Column[] =>
  canUserEdit ? defaultVisibleColumns : defaultVisibleColumns.filter(c => c !== "actions");

export const getRequiredVisibleColumns = (canUserEdit: boolean): Column[] =>
  canUserEdit ? ["name", "actions"] : ["name"];

export type EditableQuantity = MakeOptional<
  Quantity & {
    usages?: ("FORMULA" | "SETUP" | "DASHBOARD")[];
  },
  "createdAt" | "updatedAt" | "ancestors" | "children" | "descendants" | "parents"
> & {
  edited?: boolean;
  usages?: ("FORMULA" | "SETUP" | "DASHBOARD")[];
};

interface P {
  projectId: string;
  quantities: EditableQuantity[];
  formulaVersions: FormulaVersion[];
  quantityIndex: QuantityIndex;
  openDeleteModal: (quantity: EditableQuantity) => () => void;
  removeNewFormula: () => void;
  onEditQuantity?: (quantity: EditableQuantity, focusFormulae?: boolean) => void;
  isQuantityPanelClosed?: boolean; // Add this prop to know if side panel is open
}

const useMetadata = ({
  projectId,
  quantities,
  quantityIndex,
  openDeleteModal,
  removeNewFormula,
  onEditQuantity,
  isQuantityPanelClosed,
  formulaVersions,
}: P) => {
  const setups = useSetups(projectId);
  const isUserSetuperOrAbove = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const canUserEdit = useCanUserDoOnProject("PROJECT_WRITE", projectId);
  const [editedDataKey, _setEditedDataKey] = React.useState<string>();
  const setEditedDataKey = React.useCallback(
    (key?: string) => {
      _setEditedDataKey(oldKey => {
        if (oldKey === computeRowKey({ id: NEW_FORMULA_TMP_ID })) {
          removeNewFormula();
        }
        return key;
      });
    },
    [_setEditedDataKey, removeNewFormula],
  );
  const revalidate = useRevalidate();

  const quantitiesEquipmentIds = useMemo(
    () => quantities.flatMap(q => q.equipmentIds ?? []),
    [quantities],
  );
  const previousQuantityEquipmentIds = usePrevious(quantitiesEquipmentIds);
  useEffect(() => {
    if (previousQuantityEquipmentIds) {
      const newEquipmentIds = quantities.flatMap(q => q.equipmentIds ?? []);
      if (newEquipmentIds.length === previousQuantityEquipmentIds.length) {
        for (const id of newEquipmentIds) {
          if (!previousQuantityEquipmentIds.includes(id)) {
            void revalidate(`/projects/:projectId(uuid)/categories`, {
              projectId,
            });
            break;
          }
        }
      } else {
        void revalidate(`/projects/:projectId(uuid)/categories`, { projectId });
      }
    }
  }, [quantities, previousQuantityEquipmentIds, projectId, revalidate]);

  const equipments = useProjectCategories(projectId, "EQUIPMENT");
  const equipmentNames = React.useCallback(
    (quantity: EditableQuantity) =>
      (
        quantity.equipmentIds
          ?.map(id => {
            const equipment = equipments.find(e => e.id === id);
            if (equipment) {
              return equipment.name;
            }
            return null;
          })
          .filter(Boolean) ?? []
      ).join(", "),
    [equipments],
  );

  const [visibleColumns, setVisibleColumns] = useColumns({
    scope: `project-${projectId}`,
  });

  const filteredColumns = useMemo(
    () =>
      visibleColumns.filter(column => {
        if (!canUserEdit && column === "actions") return false;
        if (!isUserSetuperOrAbove && column === "hidden") return false;
        return true;
      }),
    [visibleColumns, canUserEdit, isUserSetuperOrAbove],
  );

  const deferredVisibleColumns = React.useDeferredValue(filteredColumns);
  const hidden = React.useCallback(
    (column: Column) => !deferredVisibleColumns.includes(column),
    [deferredVisibleColumns],
  );

  const metadata = React.useMemo(() => {
    const base: Partial<Record<Column, ColumnMetaData<EditableQuantity>>> = {
      usage: {
        verticalAlignmentCenter: true,
        label: getLabel("usage"),
        render: q => {
          const quantity = quantityIndex[q.id];
          if (!quantity) return null;

          const isUsedInFormula = q.usages?.includes("FORMULA") ?? false;
          const isUsedInSetups = q.usages?.includes("SETUP") ?? false;
          const isUsedInDahshboard = q.usages?.includes("DASHBOARD") ?? false;

          return (
            <div className={usageIcons}>
              {isUsedInFormula && (
                <TooltipV2
                  overlay={
                    <OverlayWrapper
                      text={t("project.settings.quantities.usage_icons.tooltip.formula")}
                    />
                  }
                >
                  <span>
                    <Formula color={color.carbon800} />
                  </span>
                </TooltipV2>
              )}
              {isUsedInSetups && (
                <TooltipV2
                  overlay={
                    <OverlayWrapper
                      text={t("project.settings.quantities.usage_icons.tooltip.setup")}
                    />
                  }
                >
                  <span>
                    <Settings3Line color={color.carbon800} />
                  </span>
                </TooltipV2>
              )}
              {isUsedInDahshboard && (
                <TooltipV2
                  overlay={
                    <OverlayWrapper
                      text={t("project.settings.quantities.usage_icons.tooltip.dashboard")}
                    />
                  }
                >
                  <span>
                    <DashboardLine color={color.carbon800} />
                  </span>
                </TooltipV2>
              )}
            </div>
          );
        },
        minWidth: "68px",
        maxWidth: "68px",
        width: "68px",
      },
      name: {
        verticalAlignmentCenter: true,
        label: getLabel("name"),
        render: q => (
          <>
            <NameContainer>
              <NameEllipsis title={q.name}>{q.name}</NameEllipsis>
              <IconContainer
                title={t("setup.paragraphs.quantities.table.definitions.column.name.tooltip")}
              >
                {q.type === "FORMULA" && <PercentageIcon />}
              </IconContainer>
              <IconContainer>
                {q.locked && (
                  <TooltipV2
                    placement="top"
                    overlay={
                      <OverlayWrapper
                        text={t("setup.paragraphs.quantities.table.locked.tooltip")}
                      />
                    }
                  >
                    <span className={lockIconWrapper}>
                      <AutoMarginLockLine />
                    </span>
                  </TooltipV2>
                )}
              </IconContainer>
            </NameContainer>
          </>
        ),
        sticky: true,
        stickyBorder: true,
        minWidth: "250px",
        maxWidth: "400px",
        sortable: true,
      },
      hidden: {
        verticalAlignmentCenter: true,
        label: getLabel("hidden"),
        minWidth: "160px",
        maxWidth: "220px",
        hidden: hidden("hidden") || !isUserSetuperOrAbove,
        render: q =>
          q.hidden
            ? t("setup.paragraphs.quantities.table.definitions.column.hidden.yes")
            : t("setup.paragraphs.quantities.table.definitions.column.hidden.no"),
        sortable: true,
      },
      description: {
        verticalAlignmentCenter: true,
        label: getLabel("description"),
        render: q => q.description,
        minWidth: "200px",
        hidden: hidden("description"),
        sortable: true,
      },
      unit: {
        verticalAlignmentCenter: true,
        label: getLabel("unit"),
        minWidth: "120px",
        maxWidth: "160px",
        sortable: true,
        hidden: hidden("unit"),
        render: q => q.unit,
      },
      system: {
        verticalAlignmentCenter: true,
        label: getLabel("system"),
        hidden: hidden("system"),
        minWidth: "250px",
        sortable: true,
        render: q => q.systemId ?? "",
      },
      equipments: {
        verticalAlignmentCenter: true,
        label: getLabel("equipments"),
        hidden: hidden("equipments"),
        minWidth: "250px",
        sortable: true,
        render: q => equipmentNames(q),
      },
      equipmentType: {
        verticalAlignmentCenter: true,
        label: getLabel("equipmentType"),
        hidden: hidden("equipmentType"),
        minWidth: "250px",
        sortable: true,
        render: q => q.equipmentTypeId ?? "",
      },
      formula: {
        verticalAlignmentCenter: true,
        label: getLabel("formula"),
        minWidth: "400px",
        // maxWidth: "300px",
        hidden: hidden("formula"),
        render: q => {
          if (q.type !== "FORMULA") return null;

          const currentFormulaVersion = getCurrentFormulaVersion(q.id, formulaVersions);
          const formula = currentFormulaVersion?.formula ?? q.formula;
          const formattedFormula = isNaN(Number(formula)) ? formula : formatNumber(Number(formula));
          return (
            <FormulaContainer>
              <FormulaEllipsis>{formattedFormula}</FormulaEllipsis>
              {q.formulaVersionCount > 0 && (
                <Button
                  variant="link"
                  leftIcon={<ListCheck />}
                  onClick={() => onEditQuantity?.(q, true)}
                  disabled={isQuantityPanelClosed === false}
                >
                  {t("quantities.formula_version.version_count", q.formulaVersionCount + 1)}
                </Button>
              )}
            </FormulaContainer>
          );
        },
        sortable: true,
      },
      sensorTag: {
        verticalAlignmentCenter: true,
        label: getLabel("sensorTag"),
        minWidth: "150px",
        maxWidth: "250px",
        hidden: hidden("sensorTag"),
        render: q => <CenteredSensorDiv>{q.sensor?.tag}</CenteredSensorDiv>,
        sortable: true,
      },
      min: {
        verticalAlignmentCenter: true,
        label: getLabel("min"),
        minWidth: "120px",
        maxWidth: "250px",
        hidden: hidden("min"),
        render: q => (q.minMetrology === null ? "" : String(q.minMetrology)),
        sortable: true,
      },
      max: {
        verticalAlignmentCenter: true,
        label: getLabel("max"),
        minWidth: "120px",
        maxWidth: "250px",
        hidden: hidden("max"),
        render: q => (q.maxMetrology === null ? "" : String(q.maxMetrology)),
        sortable: true,
      },
      maxStd: {
        verticalAlignmentCenter: true,
        label: getLabel("maxStd"),
        minWidth: "160px",
        maxWidth: "200px",
        hidden: hidden("maxStd"),
        render: q => (q.maxStdMetrology === null ? "" : String(q.maxStdMetrology)),
        sortable: true,
      },
    };

    base.actions = {
      label: getLabel("actions"),
      minWidth: "100px",
      maxWidth: "100px",
      verticalAlignmentCenter: true,
      sortable: false,
      sticky: true,
      stickyBorder: true,
      stickyDirection: "right",
      render: q => {
        const quantity = quantityIndex[q.id];
        if (computeRowKey(q) === editedDataKey || !quantity) return null;

        const { hasImpactOnSetups, impactedSetupNames } = getCheckQuantityUsedInSetupsOrFormulas(
          setups,
          quantity,
        );

        const disabledDeleteFormulaManuallyLocked =
          q.formula !== null && q.locked && !isUserSetuperOrAbove;
        const disabledDeleteUsedInSetup = hasImpactOnSetups;
        const disabledDeleteSidePanelOpen = isQuantityPanelClosed === false;
        const disabledEdit = isQuantityPanelClosed === false;
        const onEdit = () => onEditQuantity?.(q);
        const onDelete = openDeleteModal(q);

        const getDeleteTooltip = () => {
          if (disabledDeleteSidePanelOpen) {
            return t("projects.actions.delete.disabled.side_panel_open");
          }

          if (disabledDeleteUsedInSetup) {
            return t(
              "projects.quantity.action.delete.disabled.has_impact_on_setups",
              impactedSetupNames,
            );
          }

          if (disabledDeleteFormulaManuallyLocked) {
            return t("projects.actions.delete.disabled.locked_formula");
          }

          return t("projects.actions.delete");
        };

        const getEditTooltip = () =>
          disabledEdit
            ? t("projects.actions.edit.disabled.side_panel_open")
            : t("projects.actions.edit");

        const getViewTooltip = () =>
          disabledEdit
            ? t("projects.actions.edit.disabled.side_panel_open")
            : t("projects.actions.view");

        return (
          <div className={actionIcons} data-cy="quantity-action-icons">
            <TooltipV2
              overlay={<OverlayWrapper text={canUserEdit ? getEditTooltip() : getViewTooltip()} />}
            >
              <div>
                <ButtonIconV2 data-cy="edit-quantity" onClick={onEdit} disabled={disabledEdit}>
                  {canUserEdit ? (
                    <PencilLine size="m" color={color.carbon800} />
                  ) : (
                    <EyeLine size="m" />
                  )}
                </ButtonIconV2>
              </div>
            </TooltipV2>
            {canUserEdit && (
              <TooltipV2
                overlay={<OverlayWrapper className={maxWidthOverlay} text={getDeleteTooltip()} />}
              >
                <div>
                  <ButtonIconV2
                    data-cy="delete-quantity"
                    onClick={onDelete}
                    disabled={
                      disabledDeleteUsedInSetup ||
                      disabledDeleteFormulaManuallyLocked ||
                      disabledDeleteSidePanelOpen
                    }
                  >
                    <DeleteBinLine size="m" color={color.carbon800} />
                  </ButtonIconV2>
                </div>
              </TooltipV2>
            )}
          </div>
        );
      },
    };

    return base as Record<Column, ColumnMetaData<EditableQuantity>>;
  }, [
    hidden,
    equipmentNames,
    editedDataKey,
    canUserEdit,
    openDeleteModal,
    isUserSetuperOrAbove,
    isQuantityPanelClosed,
    onEditQuantity,
    quantityIndex,
    setups,
    formulaVersions,
  ]);

  return {
    metadata,
    visibleColumns,
    setVisibleColumns,
    datas: quantities,
    setEditedDataKey,
  };
};

export default useMetadata;

//#region CSS
const maxWidthOverlay = css`
  max-width: 256px;
`;
const lockIconWrapper = css`
  display: inline-flex;
  margin-left: auto;
`;
const actionIcons = css`
  display: flex;
  align-items: center;
`;
const usageIcons = css`
  display: flex;
  gap: 2px;
`;
//#endregion CSS
